/** Dev21 — Story Timeline. Alternating, scroll-animated milestone timeline. */

import React from 'react';
import { motion } from 'motion/react';
import { Section, SectionHeading, Reveal } from '../components/primitives';
import { DEV21_EASE } from '../theme';
import type { Dev21Content } from '../content';

export function StoryTimelineSection({
  content,
}: {
  content: Dev21Content['story'];
}) {
  return (
    <Section id="story" tone="surface">
      <SectionHeading label={content.label} title={content.title} />

      <div className="relative mt-16">
        {/* Animated centre spine — grows as the section scrolls into view */}
        <motion.span
          aria-hidden
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.1, ease: DEV21_EASE }}
          className="absolute left-5 top-0 h-full w-px origin-top bg-gradient-to-b from-[var(--d21-accent)] via-[var(--d21-accent)]/40 to-transparent md:left-1/2"
        />

        <div className="flex flex-col gap-12 md:gap-20">
          {content.milestones.map((m, i) => {
            const flip = i % 2 === 1;
            return (
              <div
                key={`${m.year}-${m.title}`}
                className="relative grid gap-6 pl-14 md:grid-cols-2 md:items-center md:gap-12 md:pl-0"
              >
                {/* Node dot */}
                <motion.span
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: 0.15, ease: DEV21_EASE }}
                  className="absolute left-5 top-1.5 z-10 h-4 w-4 -translate-x-1/2 rounded-full border-2 border-[var(--d21-surface)] bg-[var(--d21-accent)] shadow md:left-1/2"
                />

                {/* Text */}
                <Reveal
                  className={flip ? 'md:order-2 md:pl-12' : 'md:pr-12 md:text-right'}
                >
                  <span className="font-serif text-4xl font-semibold text-[var(--d21-accent)]/30">
                    {m.year}
                  </span>
                  <h3 className="mt-1 font-serif text-2xl font-semibold text-[var(--d21-ink)]">
                    {m.title}
                  </h3>
                  <p className="mt-2 text-base leading-relaxed text-[var(--d21-muted)]">
                    {m.description}
                  </p>
                </Reveal>

                {/* Image */}
                {m.image ? (
                  <Reveal
                    delay={0.1}
                    className={flip ? 'md:order-1' : ''}
                  >
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.4, ease: DEV21_EASE }}
                      className="overflow-hidden rounded-3xl border border-[var(--d21-border)] shadow-[0_24px_60px_-36px_rgba(33,26,19,0.55)]"
                    >
                      <img
                        src={m.image}
                        alt={m.title}
                        className="aspect-[4/3] w-full object-cover"
                      />
                    </motion.div>
                  </Reveal>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}
